/**
 * Move registry. Loads and validates both fighters' move tables once, then caches the
 * per-fighter command tables. Three-free.
 *
 * Any schema violation throws HERE, at module load, which means a bad move definition fails
 * `npm run test:combat` and `npm run build` rather than producing a subtly broken fighter.
 */

import { buildMoveTable } from './schema.js';
import { buildCommandTable } from '../notation.js';
import { KESTREL_MOVES } from './kestrel.js';
import { BULWARK_MOVES } from './bulwark.js';

let _table = null;
/** @type {Map<string, object>} */
const _commandTables = new Map();

/** @returns {{ byId: Map<string, object>, all: Array<object> }} */
export function getMoveTable() {
  if (_table) return _table;
  _table = buildMoveTable([...KESTREL_MOVES, ...BULWARK_MOVES]);
  return _table;
}

/** @param {string} prefix 'ky' | 'bw' */
export function getCommandTable(prefix) {
  if (_commandTables.has(prefix)) return _commandTables.get(prefix);
  const table = buildCommandTable(getMoveTable(), prefix);
  _commandTables.set(prefix, table);
  return table;
}

/** All moves belonging to one fighter, in authoring order. */
export function movesFor(prefix) {
  return getMoveTable().all.filter((m) => m.fighterPrefix === prefix);
}

/** Every animation identifier referenced by move data. src/animation must cover all of these. */
export function requiredAnimations() {
  const set = new Set();
  for (const m of getMoveTable().all) set.add(m.anim);
  return [...set].sort();
}

/** Every audio cue referenced by move data. src/audio must handle all of these. */
export function requiredCues() {
  const set = new Set();
  for (const m of getMoveTable().all) {
    if (m.sfx.swing) set.add(m.sfx.swing);
    if (m.sfx.hit) set.add(m.sfx.hit);
    if (m.sfx.block) set.add(m.sfx.block);
  }
  return [...set].sort();
}

/** Every FX identifier referenced by move data. src/fx must handle all of these. */
export function requiredFx() {
  const set = new Set();
  for (const m of getMoveTable().all) {
    if (m.fx.hit) set.add(m.fx.hit);
    if (m.fx.trail) set.add(m.fx.trail);
  }
  return [...set].sort();
}

/** Test hook: forget the cached tables so a test can rebuild them. */
export function _resetRegistry() {
  _table = null;
  _commandTables.clear();
}

export { KESTREL_MOVES, BULWARK_MOVES };
